import request from "../../utils/request";

/**
 * CO-429 — the snapshot status arrives as a STRING, not a number.
 *
 * `Program.cs` registers `AddNewtonsoftJson` with a global `StringEnumConverter`, so every enum on
 * the wire serializes by NAME. `SnapshotStatus` (Draft = 1, Submitted = 2, Acknowledged = 3) is no
 * exception: the API sends `"Draft"` / `"Submitted"` / `"Acknowledged"`.
 *
 * This file used to declare `status: number` and the page compared it to `1` / `2`. Both
 * comparisons were permanently false, the render fell through to the "Acknowledged" branch, and the
 * Save Draft / Submit / Acknowledge action bar NEVER rendered on any snapshot.
 *
 * The fix is client-side on purpose: the string convention is global and every other consumer
 * already depends on it. `normalizeSnapshotStatus` ALSO accepts the legacy numbers so a future
 * serializer change cannot silently re-break the action bar — route every comparison through it.
 */
export type SnapshotStatus = 'Draft' | 'Submitted' | 'Acknowledged';

const NUMERIC_SNAPSHOT_STATUS: Record<number, SnapshotStatus> = {
  1: 'Draft',
  2: 'Submitted',
  3: 'Acknowledged',
};

/**
 * Coerces whatever the wire carried into the string status. Unknown input is reported as `'Draft'`
 * — the state with the fewest side effects, and the one where the manager can still act. Reading an
 * unknown value as `'Acknowledged'` is what made CO-429 invisible for so long.
 */
export function normalizeSnapshotStatus(raw: unknown): SnapshotStatus {
  if (typeof raw === 'string') {
    const match = (Object.values(NUMERIC_SNAPSHOT_STATUS) as string[]).find(
      (s) => s.toLowerCase() === raw.toLowerCase(),
    );
    if (match) return match as SnapshotStatus;
    // A numeric string ("2") is what a query-string or a hand-rolled fetch would produce.
    const parsed = Number(raw);
    if (!Number.isNaN(parsed) && NUMERIC_SNAPSHOT_STATUS[parsed]) return NUMERIC_SNAPSHOT_STATUS[parsed];
    return 'Draft';
  }
  if (typeof raw === 'number' && NUMERIC_SNAPSHOT_STATUS[raw]) return NUMERIC_SNAPSHOT_STATUS[raw];
  return 'Draft';
}

/* ──────────────────────────────────────────────────────────────────────────────────────────────
   CO-439 / CO-513 — the PERIOD contract, mirrored from the server's `SnapshotPeriod`.

   `Period` stays a `string`, and `yyyy-MM` is the ONLY shape this client understands. Every write
   has been `yyyy-MM` since CO-439; CO-513 retired the legacy `yyyy-Qn` READ path that had been
   kept alive for the rollback window.

   (CO-513 = the owner's decision 12 of 2026-08-27, "retire the legacy quarterly
   performance-snapshot code path before go-live". It has no CO- id: the ids around it were being
   consumed by concurrent sessions, so the decision itself is the durable reference. The server side
   is `Mowazaf.Server/Services/Performance/SnapshotPeriod.cs`.)

   🔴 NOTHING WAS MIGRATED and no row was rewritten. A surviving pre-CO-439 `yyyy-Qn` row still
   renders — verbatim, with the raw label it was written with, because its numbers were computed
   over a ROLLING SIX MONTHS anchored to its generation date and converting the label to a month
   would fabricate data. What it no longer does is PARSE: it keys to `''` in `periodSortKey` and
   therefore sorts LAST, it never seeds a default selection, and it is not offered as a period.
   ────────────────────────────────────────────────────────────────────────────────────────────── */

const MONTH_PERIOD_RE = /^\d{4}-(0[1-9]|1[0-2])$/;

/** True for `2026-07`. The only shape this product writes, orders or offers. */
export const isMonthPeriod = (period: string | null | undefined): boolean =>
  typeof period === 'string' && MONTH_PERIOD_RE.test(period);

/**
 * True for a stored period this client CANNOT name a month for, so it must be rendered VERBATIM.
 *
 * CO-513 replaced `isLegacyQuarterPeriod` with this. It is deliberately SHAPE-AGNOSTIC rather than a
 * `yyyy-Qn` test: retiring the legacy quarterly path did not delete the rows, and the rendering rule
 * those rows needed was never really "is it a quarter" — it was "is this a raw token rather than a
 * month name". A surviving `2026-Q3` still satisfies it, and so would any other unreadable value,
 * which is the point: a period we cannot parse is exactly the one that must not be run through
 * `formatPeriodLabel` and must not be offered as something to open.
 *
 * 🔴 IT DRIVES `dir="ltr"`, AND THAT IS LOAD-BEARING. A raw ASCII token reverses under `ar` — a DOM
 * assertion once passed on exactly that bug. `dir` is pinned ONLY here: a localized month name
 * ("يوليو 2026") is real RTL text and pinning IT to LTR would be the same mistake in the other
 * direction.
 *
 * An empty/absent period is NOT a raw token — there is nothing to pin or render — so it is false.
 */
export const isRawPeriodToken = (period: string | null | undefined): boolean =>
  typeof period === 'string' && period.length > 0 && !isMonthPeriod(period);

/**
 * CO-441 — the ONE period order, and the reason a raw string compare is not it.
 *
 * `localeCompare` puts 'Q' after every ASCII digit, so `"2026-Q3"` sorts ABOVE `"2026-08"` and
 * above every other month of that year. Measured on dev 2026-08-21, when 162 of 164 live snapshot
 * rows still carried a legacy `yyyy-Qn` period: "newest first" put a legacy quarter at the head of
 * EVERY list — which is the snapshot the employee's own page opened on and the one the Acknowledge
 * banner was addressed to, while the month they had just been notified about sat behind it,
 * unacknowledged. CO-513 retired that shape, but the class of defect is a raw string compare and
 * that has not changed: a period this client cannot read must sort LAST, never first.
 *
 * The order below is SEMANTIC, not lexical. A period is keyed as `yyyyMM` plus a one-digit SHAPE
 * RANK:
 *   `yyyy-MM`  →  `yyyyMM` + `'1'`
 *
 * 🔴 THE RANK DIGIT IS KEPT even though CO-513 left `'1'` as its only value. It is what makes the
 * key fixed-width, and the server appends the same digit; two sort keys that disagree by one
 * trailing character is exactly the list-contradicts-card defect CO-441 closed, so dropping it on
 * one side alone would reintroduce it.
 *
 * A token that is not a month keys to `''` and therefore sorts LAST. Since CO-513 that includes a
 * surviving legacy `yyyy-Qn` row, which is the right place for it: its numbers came from a rolling
 * six months anchored to its generation date, so it asserts nothing about any month, and an
 * unreadable period must never claim the "newest" slot that seeds a default selection.
 *
 * 🔴 THE SERVER IMPLEMENTS THIS SAME RULE, and the two are written to be byte-identical so they can
 * be diffed: `Mowazaf.Server/Services/Performance/SnapshotPeriod.cs` → `SortKey` / `SortKeyFor`
 * (`yyyyMM` + shape rank, unparseable → `''`). They have drifted before, and the symptom was one
 * page contradicting itself: the roster's "last reviewed" card naming a different period than the
 * list beside it for the same employee. If either side is ever changed, change both.
 *
 * Apply this to every list the client receives anyway. `GetEmployeeSnapshotsAsync` orders correctly
 * now, but arrival order is not part of any contract this module can enforce, and an SPA bundle
 * outlives the server build it was served by (ZDD rule 5).
 */
export function periodSortKey(period: string | null | undefined): string {
  // Year bounds, ONLY here — matching `SnapshotPeriod.TryParse`'s MinYear=1 / MaxYear=9998, which the
  // server needs because `new DateTime(0, …)` throws and "9999-12" overflows the moment an end
  // boundary is added. The server therefore keys those tokens to `''`; without this guard the client
  // keyed "9999-12" ABOVE everything while the server sorted it LAST — the same list-contradicts-card
  // failure this comparator exists to close, just on a value that cannot reach the Period column
  // today. Deliberately NOT folded into `isMonthPeriod`: that predicate also drives label formatting
  // and the picker, and a period we cannot ORDER is still a period we can RENDER.
  const yearInRange = (year: string): boolean => {
    const n = Number(year);
    return n >= 1 && n <= 9998;
  };

  if (isMonthPeriod(period)) {
    const [year, month] = (period as string).split('-');
    return yearInRange(year) ? `${year}${month}1` : '';
  }
  return '';
}

/**
 * Newest period first. Every `.sort` over periods on this page goes through here, and so does
 * every "which one is the newest" choice — one comparator, one place to be wrong.
 *
 * Deliberately a plain `<` over the keys rather than `localeCompare`: the keys are fixed-width
 * digit strings, and a locale-aware compare on digits is one ICU quirk away from reintroducing the
 * class this exists to close.
 */
export const comparePeriodsDesc = (a: string | null | undefined, b: string | null | undefined): number => {
  const ka = periodSortKey(a);
  const kb = periodSortKey(b);
  if (ka === kb) return 0;
  return ka < kb ? 1 : -1;
};

/**
 * `2026-07` → "July 2026", in the caller's language.
 *
 * `timeZone: 'UTC'` is load-bearing, not decoration: the period is a UTC month on the server, and
 * formatting `Date.UTC(y, m - 1, 1)` in the browser's local zone renders the PREVIOUS month for
 * every viewer behind UTC. Anything that is not a `yyyy-MM` token — above all a legacy `yyyy-Qn`
 * one — is passed through verbatim, because there is no month to name.
 */
export function formatPeriodLabel(period: string | null | undefined, language: string): string {
  if (!isMonthPeriod(period)) return period ?? '';
  const [year, month] = (period as string).split('-').map(Number);
  try {
    return new Intl.DateTimeFormat(language || 'en', {
      month: 'long',
      year: 'numeric',
      timeZone: 'UTC',
    }).format(new Date(Date.UTC(year, month - 1, 1)));
  } catch {
    // An unknown BCP-47 tag throws in `Intl`. The raw token is worse copy but it is never wrong.
    return period as string;
  }
}

/* ──────────────────────────────────────────────────────────────────────────────────────────────
   CO-443 — the four independent signals, with their denominators and the window they cover.

   They replace the composite `overallScore` as the thing a human reads. Measured on dev
   @ `4df8d48f`: a snapshot for an employee with NO DATA AT ALL rendered "25% Overall Score /
   0/0 Goals Completed / 0 Check-ins / 0 Recognitions", the model wrote that up as "notably low …
   this lack of engagement significantly impacts the overall assessment", and it was submitted to
   and acknowledged by a real person. An absent data source reached its audience as a judgement
   about a human being. Hence: no composite, and `measured: false` is a state of its own.

   `measured` is a statement about the COMPANY, not the person (the server's rule): a signal is
   unmeasured when NOBODY in the company produced a record of that kind in the period. A company
   that did produce them and a person who has none is `measured: true` with a real `0`.

   🔴 Wire shape: the server writes this column with `SummaryJsonOptions` (camelCase), like every
   other `*Summary` column — pre-serialized JSON that MVC emits verbatim. The names below are that
   contract; a mismatch reads as `undefined`, not as an error.
   ────────────────────────────────────────────────────────────────────────────────────────────── */

export interface GoalsSignal {
  measured: boolean;
  completed: number;
  total: number;
  openAtPeriodEnd: number;
}

export interface CheckInsSignal {
  measured: boolean;
  completed: number;
  withContent: number;
  avgDurationMinutes: number | null;
}

export interface RecognitionSignal {
  measured: boolean;
  received: number;
  given: number;
  /** Deliberate: `(received + given) × 10` was maximisable in an afternoon by two people. */
  distinctSenders: number;
}

export interface SkillsSignal {
  measured: boolean;
  evidenceInPeriod: number;
  distinctSkills: number;
  lifetimeSkillCount: number;
}

/**
 * The employee's own monthly reflections, counted over the same month window as every other signal.
 *
 * 🔴 A REFLECTION IS THE EMPLOYEE'S OWN WORDS, not the manager's. `withManagerResponse` is how many
 * of them the manager actually answered — which is the only part of this signal that measures the
 * MANAGER rather than the employee, and the reason it is a denominator and not the headline.
 *
 * Deliberately OUTSIDE `overallScore`: that legacy composite is frozen (see CO-443), and adding a
 * fifth source to it would silently change every historical comparison.
 */
export interface ReflectionsSignal {
  measured: boolean;
  submitted: number;
  withContent: number;
  withManagerResponse: number;
}

export interface SignalsSummary {
  period: string;
  windowStartUtc: string;
  windowEndUtc: string;
  /** The period is the CURRENT month, so the window stops at "now" and the numbers will change. */
  isInProgress: boolean;
  goals: GoalsSignal;
  checkIns: CheckInsSignal;
  recognition: RecognitionSignal;
  skills: SkillsSignal;
  /**
   * ADDED AFTER THE FACT, so it is OPTIONAL on the type and every read must tolerate its absence.
   * A snapshot generated before reflections were counted has the other four groups and not this
   * one, and that row is NOT legacy — it must keep rendering, minus this tile.
   */
  reflections?: ReflectionsSignal;
}

const asNumber = (value: unknown, fallback: number): number =>
  typeof value === 'number' && Number.isFinite(value) ? value : fallback;

const asOptionalNumber = (value: unknown): number | null =>
  typeof value === 'number' && Number.isFinite(value) ? value : null;

/**
 * Parses the `signalsSummary` column, or returns `null`.
 *
 * `null` is the LEGACY-ROW signal and callers depend on it: every snapshot written before CO-443
 * has no column value, and the card falls back to the retained `overallScore` tile for those. So
 * this must return `null` — never a zero-filled object — for anything it cannot read, or a row
 * that was simply never measured would render as four honest-looking zeroes.
 */
export function parseSignalsSummary(json: string | null | undefined): SignalsSummary | null {
  if (!json) return null;
  let raw: Record<string, unknown>;
  try {
    const parsed = JSON.parse(json);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null;
    raw = parsed as Record<string, unknown>;
  } catch {
    return null;
  }

  const goals = (raw.goals ?? {}) as Record<string, unknown>;
  const checkIns = (raw.checkIns ?? {}) as Record<string, unknown>;
  const recognition = (raw.recognition ?? {}) as Record<string, unknown>;
  const skills = (raw.skills ?? {}) as Record<string, unknown>;
  const reflections = raw.reflections as Record<string, unknown> | undefined;

  // A body carrying none of the four groups is not a signals summary at all — treat it as legacy
  // rather than reporting four unmeasured signals for a row that may simply predate the column.
  if (!raw.goals && !raw.checkIns && !raw.recognition && !raw.skills) return null;

  return {
    period: typeof raw.period === 'string' ? raw.period : '',
    windowStartUtc: typeof raw.windowStartUtc === 'string' ? raw.windowStartUtc : '',
    windowEndUtc: typeof raw.windowEndUtc === 'string' ? raw.windowEndUtc : '',
    isInProgress: raw.isInProgress === true,
    goals: {
      // An absent `measured` reads as FALSE. "Not enough signal" is the safe wrong answer here;
      // a defaulted `true` would print a zero as a measurement, which is the CO-443 defect itself.
      measured: goals.measured === true,
      completed: asNumber(goals.completed, 0),
      total: asNumber(goals.total, 0),
      openAtPeriodEnd: asNumber(goals.openAtPeriodEnd, 0),
    },
    checkIns: {
      measured: checkIns.measured === true,
      completed: asNumber(checkIns.completed, 0),
      withContent: asNumber(checkIns.withContent, 0),
      avgDurationMinutes: asOptionalNumber(checkIns.avgDurationMinutes),
    },
    recognition: {
      measured: recognition.measured === true,
      received: asNumber(recognition.received, 0),
      given: asNumber(recognition.given, 0),
      distinctSenders: asNumber(recognition.distinctSenders, 0),
    },
    skills: {
      measured: skills.measured === true,
      evidenceInPeriod: asNumber(skills.evidenceInPeriod, 0),
      distinctSkills: asNumber(skills.distinctSkills, 0),
      lifetimeSkillCount: asNumber(skills.lifetimeSkillCount, 0),
    },
    // Absent on any snapshot generated before reflections were counted. `undefined`, never a
    // zero-filled object: a defaulted `{measured:false}` would print "Not enough signal" — a claim
    // about the whole company — on a row where the question was simply never asked.
    reflections: reflections
      ? {
          measured: reflections.measured === true,
          submitted: asNumber(reflections.submitted, 0),
          withContent: asNumber(reflections.withContent, 0),
          withManagerResponse: asNumber(reflections.withManagerResponse, 0),
        }
      : undefined,
  };
}

export interface PerformanceSnapshotView {
  id: number;
  employeeId: string;
  employeeName: string;
  employeeImageUrl: string | null;
  /** CO-439 — `2026-07`. A pre-CO-439 row may still read `2026-Q3`; CO-513 no longer parses it. */
  period: string;
  strengthsSummary: string | null;
  goalsSummary: string | null;
  checkInsSummary: string | null;
  recognitionSummary: string | null;
  /**
   * CO-443 — RETAINED, and still written by the server, but no longer the headline. It is read in
   * five places inside `GetComparativeContextAsync` (score buckets, the pattern-break trigger, two
   * message strings, the peer-average RoleSignal) over a cohort that includes every pre-CO-443
   * row, so dropping it mid-wave would make the comparative panel incoherent. The card shows the
   * four signals instead; this survives only as the LEGACY fallback tile.
   */
  overallScore: number | null;
  /** CO-429 — a STRING on the wire. Compare through `normalizeSnapshotStatus`, never with `===`. */
  status: SnapshotStatus;
  decisionsSummary: string | null;
  narrativePromptsSummary: string | null;
  strengthsValidation: string | null;
  /**
   * CO-435 — the manager-owned review narrative, serialized `NarrativeData` JSON. Persisted so the
   * narrative survives a reload; before this column it lived only in browser memory and every visit
   * re-billed the AI for a fresh one.
   *
   * It is only ever written from a REAL generation or a manager's own edit. A failed AI call leaves
   * it null on purpose — a fabricated narrative in someone's performance record is worse than an
   * empty one, and the employee's read-only view renders whatever is here as their review.
   */
  narrativeSummary: string | null;
  /**
   * CO-443 — the four signals as pre-serialized JSON, or `null` on every row written before the
   * column existed. Read it through `parseSignalsSummary`; `null` is what selects the legacy
   * `overallScore` tile on the summary card.
   */
  signalsSummary: string | null;
  createdOn: string;
  updatedOn: string | null;
}

export interface SnapshotUpdateInput {
  decisionsSummary?: string | null;
  narrativePromptsSummary?: string | null;
  strengthsValidation?: string | null;
  narrativeSummary?: string | null;
}

export interface ComparativeContextView {
  insufficientData: boolean;
  snapshotCount: number;
  distributions: Record<string, { decision: string; count: number }[]>;
  patternBreaks: {
    snapshotId: number;
    employeeName: string;
    role: string;
    decision: string;
    patternContradiction: string;
    details: string;
  }[];
  roleSignals: {
    category: string;
    employeeSignal: string;
    peerPattern: string;
  }[];
}

/**
 * CO-435 — the RAW material a narrative is written from: the actual sentences people wrote, not the
 * counts. The old prompt asked the model for "behavioural patterns" while handing it four integers
 * and a list of skill names, so every narrative was a restatement of the score card.
 */
export interface NarrativeMaterialView {
  period: string;
  goals: {
    title: string;
    category: string;
    status: string;
    completedDate: string | null;
    closureNote: string | null;
  }[];
  recognitionMessages: string[];
  checkInNotes: string[];
  /**
   * DEPRECATED SHAPE, KEPT FOR ONE ROLLBACK WINDOW. The employee's reflection text with nothing
   * around it - no prompt, no date, no manager reply. `reflections` below is the same material
   * with the context that makes it usable; prefer it, and fall back to this only when the server
   * predates it (ZDD rule 5: this SPA bundle can be served by either build).
   */
  reflectionResponses: string[];
  /**
   * The employee's own monthly reflections, in context.
   *
   * OPTIONAL: absent when this bundle is talking to a server build that predates the field.
   *
   * 🔴 `responseText` IS THE EMPLOYEE'S OWN WRITING. `managerResponse` is the manager's reply to
   * it, and they are two different voices - the prompt that feeds these to the model must say
   * which is which. It used to label the whole list "Manager reflection responses", so every
   * narrative attributed the employee's self-assessment to their manager.
   */
  reflections?: {
    promptTitle: string | null;
    responseText: string | null;
    submittedAt: string | null;
    managerResponse: string | null;
  }[];
  skillNames: string[];
}

/**
 * CO-436 — the roster the employee picker is built from. It replaces `Recognition/Recipients`,
 * which hardcodes position / department / manager to `''` and therefore made those three search
 * filters permanently unmatchable.
 *
 * Scope is enforced server-side: Hr/Admin see the whole company, a manager sees only their direct
 * reports. An empty list for a caller who is neither is what puts the page in employee mode.
 */
export interface SnapshotRosterItemView {
  employeeId: string;
  fullName: string;
  title: string | null;
  departmentName: string | null;
  managerName: string | null;
  imageUrl: string | null;
  lastSnapshotOn: string | null;
  lastSnapshotPeriod: string | null;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const extractPayload = <T>(response: any): T => {
  if (!response) throw new Error("No response received");
  if (response.payload !== undefined) return response.payload;
  if (response.data && response.data.payload !== undefined) return response.data.payload;
  return response;
};

/** Applies the CO-429 normalization once, at the boundary, so no caller has to remember to. */
const normalizeSnapshot = (snapshot: PerformanceSnapshotView): PerformanceSnapshotView => ({
  ...snapshot,
  status: normalizeSnapshotStatus(snapshot?.status),
});

/**
 * GET-OR-CREATE for one MONTH, not "create". Calling it twice for the same employee and period
 * returns the SAME snapshot — the second call does not insert another Draft and does not re-bill
 * the AI. Callers must therefore treat the result as a record that may already carry decisions,
 * prompts, a narrative and a Submitted or Acknowledged status, not as a blank Draft.
 *
 * CO-439 — `period` is an OPTIONAL `yyyy-MM` query parameter, and the optionality is deliberate:
 * an old SPA bundle still in a browser tab after a swap omits it and the server falls back to the
 * last completed month (ZDD rule 5). An invalid or future month is a 400 carrying the server's own
 * sentence — do NOT coerce it here, because a silent coercion is exactly how "the month filter
 * works" gets falsely verified.
 */
export async function generateSnapshot(employeeId: string, period?: string): Promise<PerformanceSnapshotView> {
  const query = period ? `?period=${encodeURIComponent(period)}` : '';
  const response = await request.post(`PerformanceSnapshot/Generate/${employeeId}${query}`);
  return normalizeSnapshot(extractPayload<PerformanceSnapshotView>(response.data));
}

export async function getSnapshot(snapshotId: number): Promise<PerformanceSnapshotView> {
  const response = await request.get(`PerformanceSnapshot/${snapshotId}`);
  return normalizeSnapshot(extractPayload<PerformanceSnapshotView>(response.data));
}

export async function getEmployeeSnapshots(employeeId: string): Promise<PerformanceSnapshotView[]> {
  const response = await request.get(`PerformanceSnapshot/Employee/${employeeId}`);
  const payload = extractPayload<PerformanceSnapshotView[]>(response.data);
  return Array.isArray(payload) ? payload.map(normalizeSnapshot) : [];
}

export async function submitSnapshot(snapshotId: number): Promise<PerformanceSnapshotView> {
  const response = await request.put(`PerformanceSnapshot/Submit/${snapshotId}`);
  return normalizeSnapshot(extractPayload<PerformanceSnapshotView>(response.data));
}

export async function acknowledgeSnapshot(snapshotId: number): Promise<PerformanceSnapshotView> {
  const response = await request.put(`PerformanceSnapshot/Acknowledge/${snapshotId}`);
  return normalizeSnapshot(extractPayload<PerformanceSnapshotView>(response.data));
}

export async function updateSnapshot(snapshotId: number, input: SnapshotUpdateInput): Promise<PerformanceSnapshotView> {
  const response = await request.put(`PerformanceSnapshot/Update/${snapshotId}`, input);
  return normalizeSnapshot(extractPayload<PerformanceSnapshotView>(response.data));
}

export async function getComparativeContext(period: string, employeeId?: string): Promise<ComparativeContextView> {
  // CO-439 — the period is a raw URL PATH segment. `2026-07` and `2026-Q3` are both safe today,
  // but the call is one unexpected value away from a broken route, and the server compares periods
  // by exact string equality, so a mangled segment silently matches nothing instead of failing.
  const params = employeeId ? `?employeeId=${encodeURIComponent(employeeId)}` : '';
  const response = await request.get(`PerformanceSnapshot/ComparativeContext/${encodeURIComponent(period)}${params}`);
  return extractPayload<ComparativeContextView>(response.data);
}

export async function getNarrativeMaterial(snapshotId: number): Promise<NarrativeMaterialView> {
  const response = await request.get(`PerformanceSnapshot/${snapshotId}/NarrativeMaterial`);
  return extractPayload<NarrativeMaterialView>(response.data);
}

export async function getSnapshotRoster(): Promise<SnapshotRosterItemView[]> {
  const response = await request.get('PerformanceSnapshot/Roster');
  const payload = extractPayload<SnapshotRosterItemView[]>(response.data);
  return Array.isArray(payload) ? payload : [];
}
